import React, { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import clsx from 'clsx';

import { ConversationStatus, UniqueVisitor } from 'sb-lib/types';
import { getUniqueVisitor } from 'shared/api';

import { useAppDispatch, useAppSelector } from 'shared/store';
import { getVisitorDisplayName, getVisitorEmail } from 'shared/utils/visitor';
import {
  fetchConversation,
  resetConversation,
  selectVisitorConversation,
} from 'shared/store/conversation-slice';
import { selectRealtimeVisitor } from 'shared/store/realtime-slice';
import { Icon } from 'shared/components/Icon';
import Loader from 'shared/components/Loader';
import { GlobalConversationHistoryList } from '../GlobalConversationHistoryList';
import { SessionChat } from '../SessionChat';
import './style.less';
import { useImageUpload } from '../../../../shared/hooks/useImageUpload';
import { makeLog } from 'shared/utils/log';
import { ChatPopupHeader } from './Header';

interface ChatPopupProps {
  visitorId: string;
  conversationId: number;
  openConversation: (visitorId: string, conversationId: number) => void;
  closeConversation: () => void;
}

const log = makeLog('ChatPopup');

export const ChatPopup: React.FC<ChatPopupProps> = ({
  visitorId,
  conversationId,
  openConversation,
  closeConversation,
}) => {
  const dispatch = useAppDispatch();

  const activeVisitor = useAppSelector(state => selectRealtimeVisitor(state, visitorId));
  const conversation = useAppSelector(s => selectVisitorConversation(s, conversationId));
  const hasEverConnected = useRef(false);
  const { isConnectedToServer } = useAppSelector(s => s.realtime);
  useEffect(() => {
    if (isConnectedToServer) {
      hasEverConnected.current = true;
    }
  }, [isConnectedToServer]);

  const [size, setSize] = useState<'default' | 'collapsed'>('default');

  const [visitor, setVisitor] = useState<UniqueVisitor | null>(null);
  const [isFetchingVisitor, setIsFetchingVisitor] = useState(true);
  const [conversationStatus, setConversationStatus] = useState(ConversationStatus.OPEN);

  const visitorDisplayName = getVisitorDisplayName(visitor);
  const visitorEmail = getVisitorEmail(visitor);
  const visitorLocation =
    (activeVisitor && activeVisitor.location) || (visitor && visitor.lastLocation);
  const newUnreadMessages = conversation?.unreadMessageCounts.visitor || 0;

  const firstLine =
    visitorDisplayName && visitorDisplayName !== 'Unknown'
      ? visitorDisplayName
      : visitorEmail
      ? visitorEmail
      : 'Unknown';
  const secondLine =
    visitorDisplayName && visitorDisplayName !== 'Unknown' && visitorEmail
      ? visitorEmail
      : visitorLocation && visitorLocation !== 'Unknown location'
      ? visitorLocation
      : null;
  const collapsedLine =
    firstLine === 'Unknown' && secondLine && secondLine !== 'Unknown location'
      ? secondLine
      : firstLine;

  const fetchVisitor = useCallback(async () => {
    setIsFetchingVisitor(true);
    try {
      const response = await getUniqueVisitor(visitorId);
      setVisitor(response.data);
    } catch (err) {
      log.error('Failed to fetch visitor', undefined, err);
      message.error(err.message || 'Failed to fetch visitor');
    }
    setIsFetchingVisitor(false);
  }, [visitorId]);

  useEffect(() => {
    fetchVisitor();
  }, [fetchVisitor]);

  useEffect(() => {
    if (visitorId) {
      setSize('default');
      dispatch(fetchConversation({ visitorId, conversationId }));
    }
    return () => {
      dispatch(resetConversation());
    };
  }, [conversationId, dispatch, visitorId]);

  const [
    ref,
    isDragging,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleImageChange,
  ] = useImageUpload(visitorId, conversationId);

  if (isFetchingVisitor || !visitor) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={clsx('ChatPopup', `ChatPopup--${size}`)}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {hasEverConnected.current && !isConnectedToServer && (
        <div className="ChatPopup__disconnected">
          <Loader inline size="small" />
        </div>
      )}
      {!!visitor && (
        <ChatPopupHeader
          {...{
            size,
            newUnreadMessages,
            setSize,
            visitor,
            conversationId,
            collapsedLine,
            firstLine,
            secondLine,
            closeConversation,
          }}
        />
      )}
      <div className="ChatPopup__content">
        {isDragging && (
          <div className="ChatPopup__dnd">
            <div className="ChatPopup__dnd-inner">
              <div>
                <Icon icon="picture" />
                <div className="ChatPopup__dnd-title">Drag here to upload a file</div>
              </div>
            </div>
          </div>
        )}
        <div className="ChatPopup__content-conversationHistoryList">
          <GlobalConversationHistoryList
            visitorId={visitorId}
            conversationStatus={conversationStatus}
            setConversationStatus={setConversationStatus}
            openConversation={openConversation}
            size={size}
          />
        </div>
        <SessionChat
          visitor={visitor}
          size={size}
          openConversation={openConversation}
          handleImageChange={handleImageChange}
        />
      </div>
    </div>
  );
};
